import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  Dashboard,
  DashboardWidget,
  DashboardOwner,
} from '../types/widget';

interface SharedDashboardsState {
  // Shared Dashboards list
  sharedDashboards: Dashboard[];
  sharedDashboardsLoading: boolean;

  // Selected shared dashboard
  selectedDashboard: Dashboard | null;

  // Owner context for shared dashboards
  dashboardOwner: DashboardOwner | null;

  // Widgets (read-only view)
  widgets: DashboardWidget[];
  widgetsLoading: boolean;

  // Error handling
  error: string | null;
}

interface SharedDashboardsActions {
  // Shared Dashboard list actions
  setSharedDashboards: (dashboards: Dashboard[]) => void;
  setSharedDashboardsLoading: (loading: boolean) => void;

  // Selected dashboard actions
  setSelectedDashboard: (dashboard: Dashboard | null) => void;

  // Owner context actions
  setDashboardOwner: (owner: DashboardOwner | null) => void;

  // Widget actions (read-only, only local state updates)
  setWidgets: (widgets: DashboardWidget[]) => void;
  updateWidgetLocally: (id: number, updates: Partial<DashboardWidget>) => void;
  setWidgetsLoading: (loading: boolean) => void;

  // Error handling
  setError: (error: string | null) => void;
  clearError: () => void;

  // Reset
  reset: () => void;
  clearSelection: () => void;
}

type SharedDashboardsStore = SharedDashboardsState & SharedDashboardsActions;

const initialState: SharedDashboardsState = {
  sharedDashboards: [],
  sharedDashboardsLoading: false,
  selectedDashboard: null,
  dashboardOwner: null,
  widgets: [],
  widgetsLoading: false,
  error: null,
};

export const useSharedDashboardsStore = create<SharedDashboardsStore>()(
  devtools(
    (set) => ({
      ...initialState,

      // Shared Dashboard list actions
      setSharedDashboards: (dashboards) => set({ sharedDashboards: dashboards }),
      setSharedDashboardsLoading: (loading) => set({ sharedDashboardsLoading: loading }),

      // Selected dashboard actions
      setSelectedDashboard: (dashboard) => set({ selectedDashboard: dashboard }),

      // Owner context actions
      setDashboardOwner: (owner) => set({ dashboardOwner: owner }),

      // Widget actions
      setWidgets: (widgets) =>
        set(() => ({
          widgets: widgets.reduce<DashboardWidget[]>((acc, widget) => {
            return acc.find((w) => w.id === widget.id) ? acc : [...acc, widget];
          }, []),
        })),

      updateWidgetLocally: (id, updates) =>
        set((state) => ({
          widgets: state.widgets.map((w) =>
            String(w.id) === String(id) ? { ...w, ...updates } : w
          ),
        })),

      setWidgetsLoading: (loading) => set({ widgetsLoading: loading }),

      // Error handling
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),

      // Reset
      reset: () => set(initialState),

      // Clear selection (go back to list)
      clearSelection: () =>
        set({
          selectedDashboard: null,
          dashboardOwner: null,
          widgets: [],
        }),
    }),
    { name: 'shared-dashboards-store' }
  )
);
